// src/lib/aeroapiCache.ts — TTL-aware IDB cache for AeroAPI responses.
//
// Wraps the existing kvGet/kvSet primitives from idb.ts with an
// envelope that carries `cachedAt` (unix ms) so callers can decide
// whether the payload is "fresh" vs "stale" vs non-existent.
//
// Usage:
//   const entry = await aeroapiGet<RouteResponse>('routes|KSFO|KSMF');
//   if (entry) {
//     renderUI(entry.data);
//     if (entry.isStale(TTL_12H)) refetchInBackground();
//   }
//   aeroapiSet('routes|KSFO|KSMF', data);
//
// The key convention is up to the caller — prefix with the feature
// name so future features don't collide.
//
// All IDB errors are caught and treated as cache-miss to keep callers
// simple. Failures never propagate to the UI layer.

import { kvGet, kvSet } from './idb.js';

const TTL_12H = 12 * 60 * 60 * 1000; // 12 hours in ms

export interface CacheEntry<T> {
  data: T;
  cachedAt: number; // Date.now() when written
  /** true when the entry exists but is older than the given TTL */
  isStale: (ttlMs?: number) => boolean;
  /** human-readable label like "Mon 09:30" for staleness badge */
  label: string;
}

interface RawEntry<T> {
  data: T;
  cachedAt: number;
}

function makeEntry<T>(raw: RawEntry<T>): CacheEntry<T> {
  return {
    data: raw.data,
    cachedAt: raw.cachedAt,
    isStale: (ttlMs = TTL_12H) => Date.now() - raw.cachedAt >= ttlMs,
    label: formatLabel(raw.cachedAt),
  };
}

function formatLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hh}:${mm}`;
  if (diffDays === 0) return timeStr;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[d.getDay()]} ${timeStr}`;
}

export async function aeroapiGet<T>(key: string): Promise<CacheEntry<T> | null> {
  try {
    const raw = await kvGet<RawEntry<T>>(`aeroapi:${key}`);
    if (!raw || typeof raw !== 'object' || !('data' in raw) || !('cachedAt' in raw)) return null;
    return makeEntry(raw as RawEntry<T>);
  } catch {
    return null;
  }
}

export async function aeroapiSet<T>(key: string, data: T): Promise<void> {
  try {
    const raw: RawEntry<T> = { data, cachedAt: Date.now() };
    await kvSet(`aeroapi:${key}`, raw);
  } catch {
    // IDB write failure is silent — we just won't have a cache entry.
  }
}
